//https://www.patterns.dev/posts/observer-pattern
import { devLog } from '@/utils/dev';

type Observer<TData> = (__data: TData) => void | Promise<void>;

interface NotifyOptions<TData> {
  data: TData;
  onComplete?: () => void;
  onError?: (__errors: Error[]) => void;
}

class ObserverFactory<TData = any> {
  private observers: Observer<TData>[];

  constructor() {
    this.observers = [];
  }

  subscribe(f: Observer<TData>): () => void {
    this.observers.push(f);
    return () => this.unsubscribe(f);
  }

  unsubscribe(f: Observer<TData>) {
    this.observers = this.observers.filter((observer) => observer !== f);
  }

  /**
   * Notifies all subscribed observers with the provided data.
   * Handles both synchronous and asynchronous observers.
   * @param options - Object containing data and optional callbacks
   */
  async notify(options: NotifyOptions<TData>) {
    const { data, onComplete, onError } = options;
    const errors: Error[] = []; // Array to collect errors from failing observers

    // Notify all observers and handle errors
    await Promise.allSettled(
      this.observers.map(async (observer) => {
        try {
          await observer(data); // Call the observer with the provided data
        } catch (error) {
          errors.push(error as Error); // Collect errors if the observer fails
        }
      })
    );

    // If there are errors, invoke the onError callback with the list of errors
    if (errors.length > 0) {
      devLog('Errors@notify', errors); // Log errors for debugging
      if (onError) onError(errors);
    }
    // If all observers invoked, invoke the onComplete callback
    if (onComplete) {
      onComplete();
    }
  }
}

export default ObserverFactory;
